import { SaiQuickTourStep } from 'app/ui/sai-quick-tour/sai-quick-tour.types';

/** i18n keys for the ordered completion summary (short labels). */
export const TOUR_COMPLETE_FLOW_SEGMENT_KEYS = [
    'Department',
    'Data',
    'Agent',
    'Chat',
    'Tracker',
] as const;

const PROFILE_STEP_IDS = new Set([
    'profile',
    'hierarchy-tab',
    'add-division',
    'add-department',
]);

type TourFlowSegmentId = 'department' | 'data' | 'agent' | 'chat' | 'tracker';

const TOUR_FLOW_SEGMENT_DEFS: ReadonlyArray<{
    id: TourFlowSegmentId;
    labelKey: (typeof TOUR_COMPLETE_FLOW_SEGMENT_KEYS)[number];
    isPresent: (steps: readonly SaiQuickTourStep[]) => boolean;
}> = [
    {
        id: 'department',
        labelKey: 'Department',
        isPresent: steps => steps.some(step => PROFILE_STEP_IDS.has(step.id)),
    },
    {
        id: 'data',
        labelKey: 'Data',
        isPresent: steps => steps.some(step => step.permissionKey === 'data_source'),
    },
    {
        id: 'agent',
        labelKey: 'Agent',
        isPresent: steps => steps.some(step => step.permissionKey === 'agents'),
    },
    {
        id: 'chat',
        labelKey: 'Chat',
        isPresent: steps => steps.some(step => step.id === 'chat-nav'),
    },
    {
        id: 'tracker',
        labelKey: 'Tracker',
        isPresent: steps => steps.some(step => step.permissionKey === 'trackers'),
    },
];

/** Label keys for modules present in this run, in canonical tour order. */
export function getTourCompleteFlowSegmentKeys(
    steps: readonly SaiQuickTourStep[],
): readonly string[] {
    return TOUR_FLOW_SEGMENT_DEFS.filter(segment => segment.isPresent(steps)).map(
        segment => segment.labelKey,
    );
}

/** Attach `bodyParams.flowSegmentKeys` on the complete step for dynamic copy. */
export function applyCompleteStepBody(
    steps: readonly SaiQuickTourStep[],
): readonly SaiQuickTourStep[] {
    const flowSegmentKeys = getTourCompleteFlowSegmentKeys(steps);
    return steps.map(step =>
        step.id === 'complete'
            ? {
                  ...step,
                  bodyKey: 'Quick tour — complete body',
                  bodyParams: { flowSegmentKeys },
              }
            : step,
    );
}
